import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PhotoIcon,
  XMarkIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  TagIcon,
  DocumentTextIcon,
  EyeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi, CreateListingDto } from '../../api/marketplace';
import { birdsApi, Bird, BirdStatus } from '../../api/birds';
import { speciesApi, Species } from '../../api/species';
import { geneticsApi } from '../../api/genetics';
import { customersApi, Customer, CustomerStatus } from '../../api/customers';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalConfig } from '../../hooks/useGlobalConfig';
import toast from 'react-hot-toast';
import PhotoSelector from './PhotoSelector';
import AmountInput from '../ui/AmountInput';

// Remove the local Bird interface since we're using the one from birds API
const PLATFORMS = [
      { id: 'whatsapp', name: 'WhatsApp', icon: '💬' },
  { id: 'facebook', name: 'Facebook', icon: '📘' },
  { id: 'instagram', name: 'Instagram', icon: '📷' },
  { id: 'telegram', name: 'Telegram', icon: '📱' },
  { id: 'birdMarketplace', name: 'Bird Marketplace', icon: '🦜' },
];

export default function CreateListingForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const [loading, setLoading] = useState(false);
  const [availableBirds, setAvailableBirds] = useState<Bird[]>([]);
  const [loadingBirds, setLoadingBirds] = useState(false);
  const [speciesData, setSpeciesData] = useState<Species[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [showSpeciesInfo, setShowSpeciesInfo] = useState(false);
  const [formData, setFormData] = useState<CreateListingDto>({
    birdId: '',
    platforms: [],
    title: '',
    description: '',
    highlights: [''],
    specifications: {
      species: '',
      age: '',
      gender: '',
      color: '',
      size: '',
      healthStatus: '',
      breedingHistory: '',
      certifications: [],
    },
    price: {
      amount: 0,
      currency: '',
      negotiable: false,
      originalPrice: 0,
      discountPercentage: 0,
    },
    media: {
      primaryImage: '',
      gallery: [],
      video: '',
      documents: [],
    },
    metadata: {
      tags: [],
      categories: [],
      location: '',
      shippingOptions: [],
    },
  });

  const facilityId = 'default'; // Use a default facility ID, backend will handle facility resolution

  useEffect(() => {
    loadAvailableBirds();
    loadSpeciesData();
    loadCustomers();
  }, []);

  const loadSpeciesData = async () => {
    try {
      setLoadingSpecies(true);
      const response = await speciesApi.getAll();
      setSpeciesData(response.data || []);
    } catch (error) {
      console.error('Failed to load species data:', error);
      toast.error('Failed to load species information');
    } finally {
      setLoadingSpecies(false);
    }
  };

  const loadAvailableBirds = async () => {
    try {
      setLoadingBirds(true);
      
      // Load real birds from the API that are available for sale
      const birdsData = await birdsApi.getBirds();
      
      // Filter birds that are specifically marked FOR_SALE
      const availableBirds = birdsData.filter((bird: Bird) => 
        bird.status === BirdStatus.FOR_SALE
      );
      
      setAvailableBirds(availableBirds);
      
      if (availableBirds.length === 0) {
        toast.success('No birds marked for sale. Update bird status to "FOR_SALE" in the Birds module first.');
      }
    } catch (error) {
      console.error('Error loading birds:', error);
      toast.error('Failed to load available birds');
    } finally {
      setLoadingBirds(false);
    }
  };

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      
      // Load active customers for potential sales
      const customersData = await customersApi.getAll();
      const activeCustomers = customersData.filter((customer: Customer) => 
        customer.status === CustomerStatus.ACTIVE
      );
      
      setCustomers(activeCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSpecificationChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [field]: value,
      },
    }));
  };

  const handlePriceChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      price: {
        ...prev.price,
        [field]: value,
      },
    }));
  };

  const handleMediaChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      media: {
        ...prev.media,
        [field]: value,
      },
    }));
  };

  const handleMetadataChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value,
      },
    }));
  };

  const handlePlatformToggle = (platformId: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId],
    }));
  };

  const handleHighlightChange = (index: number, value: string) => {
    const newHighlights = [...formData.highlights];
    newHighlights[index] = value;
    setFormData(prev => ({
      ...prev,
      highlights: newHighlights,
    }));
  };

  const addHighlight = () => {
    setFormData(prev => ({
      ...prev,
      highlights: [...prev.highlights, ''],
    }));
  };

  const removeHighlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index),
    }));
  };

  const handleBirdSelect = async (birdId: string) => {
    const selectedBird = availableBirds.find(bird => bird.id === birdId);
    if (selectedBird) {
      const species = speciesData.find(s => s.id === selectedBird.speciesId);
      setSelectedSpecies(species || null);

      let geneticsProfile: Awaited<ReturnType<typeof geneticsApi.getBirdListingProfile>> | null = null;
      try {
        geneticsProfile = await geneticsApi.getBirdListingProfile(birdId);
      } catch {
        geneticsProfile = null;
      }

      const mutationLabel =
        geneticsProfile?.mutationName || selectedBird.mutationName || 'Standard';
      const phenotypeLabel =
        geneticsProfile?.phenotype || selectedBird.phenotype || mutationLabel;
      const listingTags = geneticsProfile?.tags?.length
        ? geneticsProfile.tags
        : [selectedBird.mutationName].filter(Boolean) as string[];

      const age = selectedBird.hatchingDate ?
        Math.floor((new Date().getTime() - new Date(selectedBird.hatchingDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25)) :
        0;

      setFormData(prev => ({
        ...prev,
        birdId,
        title: `${species?.commonName || 'Bird'} - ${mutationLabel}`,
        description: species ?
          `${species.commonName} (${species.genus} ${species.species}) — ${phenotypeLabel}. ${species.diet}. Origin: ${species.originLocation}.` :
          `${mutationLabel} bird available for sale.`,
        specifications: {
          ...prev.specifications,
          species: species?.commonName || '',
          age: `${age} years`,
          gender: selectedBird.gender || '',
          color: phenotypeLabel || species?.colorDescription || '',
          size: species?.size ? `${species.size}cm` : '',
          healthStatus: selectedBird.healthStatus || '',
          breedingHistory: species?.breedingDifficultyLevel || '',
          certifications: species?.citesStatus ? [`CITES: ${species.citesStatus}`] : [],
        },
        price: {
          ...prev.price,
          amount: selectedBird.sellPrice || species?.priceRangeMin || 0,
        },
        metadata: {
          ...prev.metadata,
          tags: [
            species?.commonName,
            species?.genus,
            species?.typeCategory,
            species?.originLocation,
            ...listingTags,
            species?.iucnStatus,
            species?.citesStatus,
          ].filter(Boolean) as string[],
          categories: [species?.typeCategory, species?.originLocation].filter(Boolean) as string[],
        },
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.birdId) {
      toast.error('Please select a bird');
      return;
    }

    if (!formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    try {
      setLoading(true);
      
      // Filter out empty highlights
      const filteredHighlights = formData.highlights.filter(h => h.trim() !== '');
      
      const listingData = {
        ...formData,
        highlights: filteredHighlights,
      };

      await marketplaceApi.createListing(facilityId, listingData);
      
      toast.success('Listing created successfully!');
      navigate('/marketplace');
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error('Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/80 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-slate-900">Create New Listing</h2>
            <p className="mt-1 text-sm text-slate-500">
              Create a new marketplace listing for your bird
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Bird Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Bird *
              </label>
              {loadingBirds ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-slate-500">Loading available birds...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <select
                    value={formData.birdId}
                    onChange={(e) => handleBirdSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a bird</option>
                    {availableBirds.map((bird) => {
                      const species = speciesData.find(s => s.id === bird.speciesId);
                      return (
                        <option key={bird.id} value={bird.id}>
                          {species?.commonName || 'Unknown Species'} - {bird.mutationName || 'Standard'} ({bird.bandId})
                        </option>
                      );
                    })}
                  </select>
                  
                  {/* Species Information Panel */}
                  {selectedSpecies && (
                    <div className="mt-4 p-4 bg-cyan-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-blue-900">Species Information</h4>
                        <button
                          type="button"
                          onClick={() => setShowSpeciesInfo(!showSpeciesInfo)}
                          className="text-cyan-600 hover:text-cyan-800"
                        >
                          <InformationCircleIcon className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-cyan-800">Scientific Name:</span>
                          <p className="text-cyan-700">{selectedSpecies.genus} {selectedSpecies.species}</p>
                        </div>
                        <div>
                          <span className="font-medium text-cyan-800">Origin:</span>
                          <p className="text-cyan-700">{selectedSpecies.originLocation}</p>
                        </div>
                        <div>
                          <span className="font-medium text-cyan-800">Size:</span>
                          <p className="text-cyan-700">{selectedSpecies.size}cm</p>
                        </div>
                        <div>
                          <span className="font-medium text-cyan-800">Weight:</span>
                          <p className="text-cyan-700">{selectedSpecies.weight}g</p>
                        </div>
                        <div>
                          <span className="font-medium text-cyan-800">IUCN Status:</span>
                          <p className="text-cyan-700">{selectedSpecies.iucnStatus}</p>
                        </div>
                        <div>
                          <span className="font-medium text-cyan-800">CITES Status:</span>
                          <p className="text-cyan-700">{selectedSpecies.citesStatus}</p>
                        </div>
                      </div>
                      
                      {showSpeciesInfo && (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-cyan-800">Diet:</span>
                              <p className="text-cyan-700">{selectedSpecies.diet}</p>
                            </div>
                            <div>
                              <span className="font-medium text-cyan-800">Housing:</span>
                              <p className="text-cyan-700">{selectedSpecies.recommendedHousing}</p>
                            </div>
                            <div>
                              <span className="font-medium text-cyan-800">Lifespan:</span>
                              <p className="text-cyan-700">{selectedSpecies.longevity} years</p>
                            </div>
                            <div>
                              <span className="font-medium text-cyan-800">Breeding Difficulty:</span>
                              <p className="text-cyan-700">{selectedSpecies.breedingDifficultyLevel || 'Not specified'}</p>
                            </div>
                            {selectedSpecies.marketValueRange && (
                              <div>
                                <span className="font-medium text-cyan-800">Market Value:</span>
                                <p className="text-cyan-700">{selectedSpecies.marketValueRange}</p>
                              </div>
                            )}
                            {selectedSpecies.commonHealthIssues && (
                              <div>
                                <span className="font-medium text-cyan-800">Health Considerations:</span>
                                <p className="text-cyan-700">{selectedSpecies.commonHealthIssues}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Platforms */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Platforms *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    type="button"
                    onClick={() => handlePlatformToggle(platform.id)}
                    className={`p-3 border rounded-lg text-left transition-colors ${
                      formData.platforms.includes(platform.id)
                        ? 'border-blue-500 bg-cyan-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{platform.icon}</span>
                      <span className="font-medium">{platform.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter listing title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Price *
                </label>
                <div className="flex space-x-2">
                  <AmountInput
                    value={formData.price.amount}
                    onChange={(v) => handlePriceChange('amount', v ?? 0)}
                    inputClassName="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    min={0}
                    required
                  />
                  <select
                    value={formData.price.currency || config?.currency?.default || 'ZAR'}
                    onChange={(e) => handlePriceChange('currency', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ZAR">ZAR (R)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="JPY">JPY (¥)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the bird, its characteristics, and any special features..."
                required
              />
            </div>

            {/* Highlights */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Highlights
              </label>
              <div className="space-y-2">
                {formData.highlights.map((highlight, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={highlight}
                      onChange={(e) => handleHighlightChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter a highlight..."
                    />
                    {formData.highlights.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHighlight(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addHighlight}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Highlight
                </button>
              </div>
            </div>

            {/* Specifications */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Species
                </label>
                <input
                  type="text"
                  value={formData.specifications.species}
                  onChange={(e) => handleSpecificationChange('species', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., African Grey"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Age
                </label>
                <input
                  type="text"
                  value={formData.specifications.age}
                  onChange={(e) => handleSpecificationChange('age', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 2 years"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Gender
                </label>
                <select
                  value={formData.specifications.gender}
                  onChange={(e) => handleSpecificationChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select gender...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Color
                </label>
                <input
                  type="text"
                  value={formData.specifications.color}
                  onChange={(e) => handleSpecificationChange('color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Size
                </label>
                <select
                  value={formData.specifications.size}
                  onChange={(e) => handleSpecificationChange('size', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select size...</option>
                  <option value="Small">Small</option>
                  <option value="Medium">Medium</option>
                  <option value="Large">Large</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Health Status
                </label>
                <select
                  value={formData.specifications.healthStatus}
                  onChange={(e) => handleSpecificationChange('healthStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select health status...</option>
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>
            </div>

            {/* Breeding History */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Breeding History
              </label>
              <textarea
                value={formData.specifications.breedingHistory}
                onChange={(e) => handleSpecificationChange('breedingHistory', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe breeding history, if any..."
              />
            </div>

            {/* Price Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Original Price
                </label>
                <AmountInput
                  value={formData.price.originalPrice}
                  onChange={(v) => handlePriceChange('originalPrice', v ?? 0)}
                  inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  min={0}
                  allowEmpty
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Discount Percentage
                </label>
                <input
                  type="number"
                  value={formData.price.discountPercentage}
                  onChange={(e) => handlePriceChange('discountPercentage', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                  max="100"
                  step="1"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.price.negotiable}
                    onChange={(e) => handlePriceChange('negotiable', e.target.checked)}
                    className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-slate-700">Price negotiable</span>
                </label>
              </div>
            </div>

            {/* Media */}
            <PhotoSelector
              value={formData.media.primaryImage}
              onChange={(url) => handleMediaChange('primaryImage', url)}
              birdId={formData.birdId}
              placeholder="Select or upload a primary photo"
            />

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.metadata.location}
                onChange={(e) => handleMetadataChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., New York, NY"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={formData.metadata.tags.join(', ')}
                onChange={(e) => handleMetadataChange('tags', e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., rare, hand-fed, talking"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/marketplace')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Listing
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
